/**
 * Alerts.tsx — Critical and high-severity findings across all customers.
 *
 * Aggregates the most urgent findings and surfaces them with filtering
 * by severity, provider, and customer.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';
import { useAuth } from '../context/AuthContext';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Customer { id: string; name: string; }
interface ScanSummary { id: string; customer_id: string; provider: string; }
interface Finding {
  id: string;
  title: string;
  description: string;
  severity: string;
  resource_id: string;
  resource_type: string;
  remediation: string;
}
interface AlertRow extends Finding {
  scan_id: string;
  customer_name: string;
  provider: string;
}

// ── Severity colours ──────────────────────────────────────────────────────────

type ChipColor = 'error' | 'warning' | 'info' | 'default';

function severityColor(severity: string): ChipColor {
  switch (severity) {
    case 'critical': return 'error';
    case 'high':     return 'error';
    case 'medium':   return 'warning';
    case 'low':      return 'info';
    default:         return 'default';
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Alerts() {
  const { isAdmin } = useAuth();
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');

  useEffect(() => {
    const load = async () => {
      try {
        const scansRes = await axios.get<(ScanSummary & { findings: Finding[] })[]>(
          `${API_BASE}/v1/scans/`,
        );

        // Only admins may call /v1/customers/ — customer/partner roles fetch
        // their own scans only (already server-side scoped) and don't need the
        // full customer list.
        const customerMap: Record<string, string> = {};
        if (isAdmin) {
          const customersRes = await axios.get<Customer[]>(`${API_BASE}/v1/customers/`);
          customersRes.data.forEach((c) => { customerMap[c.id] = c.name; });
        }

        const rows: AlertRow[] = [];
        for (const scan of scansRes.data) {
          for (const finding of scan.findings) {
            if (['critical', 'high', 'medium'].includes(finding.severity)) {
              rows.push({
                ...finding,
                scan_id: scan.id,
                customer_name: customerMap[scan.customer_id] ?? 'My Organization',
                provider: scan.provider,
              });
            }
          }
        }

        // Sort: critical → high → medium, then by title
        const order: Record<string, number> = { critical: 0, high: 1, medium: 2 };
        rows.sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9));

        setAlerts(rows);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin]);

  const providers = Array.from(new Set(alerts.map((a) => a.provider)));

  const filtered = alerts.filter((a) => {
    if (severityFilter !== 'all' && a.severity !== severityFilter) return false;
    if (providerFilter !== 'all' && a.provider !== providerFilter) return false;
    return true;
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Alerts</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Critical and high-severity findings requiring attention
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Severity</InputLabel>
          <Select value={severityFilter} label="Severity" onChange={(e) => setSeverityFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={providerFilter} label="Provider" onChange={(e) => setProviderFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {providers.map((p) => <MenuItem key={p} value={p}>{p.toUpperCase()}</MenuItem>)}
          </Select>
        </FormControl>
        <Box display="flex" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {filtered.length} alert{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Stack>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: 'background.default' } }}>
                <TableCell>Severity</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Resource</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((alert) => (
                <TableRow key={alert.id} hover>
                  <TableCell>
                    <Chip
                      label={alert.severity.toUpperCase()}
                      color={severityColor(alert.severity)}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{alert.title}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {alert.remediation}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{alert.customer_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={alert.provider.toUpperCase()} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {alert.resource_id || alert.resource_type || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {alerts.length === 0 ? 'No alerts found — great news!' : 'No results match the current filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
